import type { PlotData, SelectionContext } from "@/types/plot";
import { resolveAssetClass, type PropertyScore } from "./propertyScore";

// ═══════════════════════════════════════════════════════════════════════════
//  Score calibration — turns an absolute composite into a relative position.
//
//  WHY: the composite is a weighted mean over ~40 factors, ~70% of whose
//  weight is area-level data that neighbouring plots share. Measured on the
//  live engine (2026-07 sweep): the best-vs-worst house on the same street
//  spans only ~12 points, a typical adjacent pair 2–5. The absolute number is
//  a regional instrument, so on its own "57 vs 61" reads as "basically the
//  same" even when it's the gap between a mid-market and a top-quartile home.
//  Showing the estimated national percentile alongside restores the contrast
//  the composite scale compresses.
//
//  CALIBRATION v1 is an estimated curve, not an empirical CDF. Anchors are
//  pinned to measured live-engine outputs for designed archetypes:
//    33 → deliberately weak plot (1958 F-label row house, weak area)  ≈ p5
//    59 → deliberately typical plot (1982 C-label, average area)      ≈ p50
//    81 → deliberately prime plot (2016 A+ detached, prime area)      ≈ p97
//  with intermediate anchors interpolated to keep the curve smooth. When real
//  scored-plot telemetry accumulates, refit the table and bump the version —
//  scripts/test-scoring.ts snapshots the curve so any change is explicit.
// ═══════════════════════════════════════════════════════════════════════════

export const SCORE_CALIBRATION_VERSION = 1;

// ── The score ladder ────────────────────────────────────────────────────────
// ONE set of cut-points for the WORD a score wears and the COLOUR it wears.
// Until 2026-09 these were two ladders: words switched at 80/65/50/35 while
// every colour helper (capsule, ring, overview, chips, compare, factor card —
// eight private copies) switched at 75/60/45/30. So a 61 was "Fair" in teal
// and a 56 was "Fair" in amber, and a 79 was green "Strong" — the colour
// could not be trusted as a quick read of the tier. Now band 5..1 IS the tier,
// and every surface derives from `scoreBand`.
//
//   band 5  ≥ 80  Excellent   emerald
//   band 4  ≥ 65  Strong      teal
//   band 3  ≥ 50  Fair        amber
//   band 2  ≥ 35  Weak        orange
//   band 1  < 35  Poor        red
//
// Two renderings of the same band:
//   `scoreInk()`  → `rgb(var(--c-score-N))`, the THEMED token — for anything
//                   painted as text/stroke/fill on a themed surface. Dark
//                   values are the historical hexes, light values are re-tuned
//                   to clear AA on paper (the raw dark teal is 2:1 on paper).
//   `scoreHex()`  → the fixed dark hex — ONLY for data encodings that must
//                   survive a theme flip or a PNG export (map fills, canvas).
//                   Never paint this as ink on a themed surface.
// ────────────────────────────────────────────────────────────────────────────

export type ScoreBand = 1 | 2 | 3 | 4 | 5;

/** Lower bound of bands 5, 4, 3, 2 (band 1 is everything below). */
export const SCORE_BAND_FLOORS: readonly [number, number, number, number] = [80, 65, 50, 35];

export function scoreBand(s: number): ScoreBand {
  const [b5, b4, b3, b2] = SCORE_BAND_FLOORS;
  if (s >= b5) return 5;
  if (s >= b4) return 4;
  if (s >= b3) return 3;
  if (s >= b2) return 2;
  return 1;
}

const TIER_WORD: Record<ScoreBand, string> = {
  5: "Excellent",
  4: "Strong",
  3: "Fair",
  2: "Weak",
  1: "Poor",
};

/** The canonical dark-theme hex per band — `--c-score-N`'s dark values
 *  (globals.css). Brand greens on top, muted warm rungs below; keep the two
 *  in step. */
export const SCORE_BAND_HEX: Record<ScoreBand, string> = {
  5: "#7BD8A0",
  4: "#4CAF7A",
  3: "#C9A961",
  2: "#D08A5A",
  1: "#D9706A",
};

/**
 * Tier word for a composite — the ONE source shared by the app UI
 * (ScoreCapsule, ScoreOverview, the ring, the factor card) and the PDF
 * dossier. The PDF previously kept its own ladder (Exceptional/Solid/Mixed at
 * different thresholds), so the same score wore different words in the app
 * and in the emailed report.
 */
export function scoreTier(s: number): string {
  return TIER_WORD[scoreBand(s)];
}

/** Themed colour for a score painted as ink/stroke/fill on a themed surface. */
export function scoreInk(s: number): string {
  return `rgb(var(--c-score-${scoreBand(s)}))`;
}

/** Fixed hex for a score used as a DATA fill (theme-exempt, export-stable). */
export function scoreHex(s: number): string {
  return SCORE_BAND_HEX[scoreBand(s)];
}

/** [composite, estimated % of Dutch residential properties scoring below]. */
const NL_RESIDENTIAL_ANCHORS: [number, number][] = [
  [20, 1],
  [33, 5],
  [45, 18],
  [52, 32],
  [58, 48],
  [64, 68],
  [70, 82],
  [76, 92],
  [81, 97],
  [88, 99],
];

/**
 * Estimated share (1–99, integer %) of Dutch residential properties that
 * score below the given composite. Piecewise-linear over the anchor table,
 * clamped at the ends. Monotonic by construction.
 */
export function percentileForComposite(composite: number): number {
  const a = NL_RESIDENTIAL_ANCHORS;
  if (composite <= a[0][0]) return a[0][1];
  if (composite >= a[a.length - 1][0]) return a[a.length - 1][1];
  for (let i = 1; i < a.length; i++) {
    if (composite <= a[i][0]) {
      const [x0, y0] = a[i - 1];
      const [x1, y1] = a[i];
      const t = (composite - x0) / (x1 - x0);
      return Math.round(y0 + t * (y1 - y0));
    }
  }
  return a[a.length - 1][1];
}

export interface PercentileChip {
  /** Short label for tight UI slots, e.g. "Top 27%", "Mid-market", "Bottom 37%". */
  text: string;
  /** Hover/long-form explanation, includes the estimation caveat. */
  title: string;
  /** Underlying % of NL homes scoring below this composite. */
  percentBelow: number;
}

/**
 * Relative-position chip for a scored plot, or null when a percentile would
 * be misleading and must not be shown:
 *  - excluded plots (memorials, schools, …) — no market position to rank
 *  - non-residential scopes — the v1 curve is calibrated on homes only
 *  - low-confidence scores (< 0.3) — the composite is shrunk toward 50, so
 *    its percentile would mostly rank the shrinkage, not the property
 */
export function percentileChip(
  score: PropertyScore,
  plot: PlotData,
  ctx?: SelectionContext,
): PercentileChip | null {
  if (score.exclusion && score.exclusion.tier !== "caveat") return null;
  if (score.confidence < 0.3) return null;
  const { assetClass } = resolveAssetClass(plot, ctx);
  if (assetClass !== "residential") return null;

  const percentBelow = percentileForComposite(score.composite);
  const text =
    percentBelow >= 55 ? `Top ${100 - percentBelow}%`
    : percentBelow <= 45 ? `Bottom ${percentBelow}%`
    : "Mid-market";
  return {
    text,
    title: `Scores higher than an estimated ${percentBelow}% of Dutch residential properties (calibration v${SCORE_CALIBRATION_VERSION}).`,
    percentBelow,
  };
}
